import type { Channel } from './score/scoring';

/**
 * Local-only progression store.
 *
 * Deliberately not a server: the moment session data leaves the device this
 * stops being a thing you can honestly describe as private, and the retention
 * value of a leaderboard does not outweigh that for a product whose pitch is
 * "point your webcam at yourself in your bedroom".
 *
 * What is stored is what a coach would write in a notebook: per-channel ratings
 * over time, session counts, and personal bests. That is enough for streaks,
 * skill curves and adaptive drill selection.
 */

const KEY = 'vs.progress.v1';

export interface SessionRecord {
  /** Epoch ms. */
  at: number;
  opponent: string;
  difficulty: string;
  rounds: number;
  overall: number;
  cards: { player: number; ai: number };
  landed: number;
  thrown: number;
  absorbed: number;
  channels: Record<string, number>;
}

export interface ProgressState {
  sessions: SessionRecord[];
  /** Consecutive calendar days with at least one session. */
  streak: number;
  lastSessionDay: string;
  best: { overall: number; combo: string; counters: number };
  totals: { rounds: number; punches: number; minutes: number };
}

function empty(): ProgressState {
  return {
    sessions: [],
    streak: 0,
    lastSessionDay: '',
    best: { overall: 0, combo: '', counters: 0 },
    totals: { rounds: 0, punches: 0, minutes: 0 },
  };
}

export function load(): ProgressState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return empty();
    return { ...empty(), ...(JSON.parse(raw) as ProgressState) };
  } catch {
    return empty();
  }
}

export function save(s: ProgressState): void {
  try {
    // Cap history: 200 sessions is ~18 months of daily use and keeps the blob
    // well under any storage quota worth worrying about.
    if (s.sessions.length > 200) s.sessions = s.sessions.slice(-200);
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* storage disabled — progression is a nicety, not a requirement */
  }
}

const dayKey = (t: number) => new Date(t).toISOString().slice(0, 10);

export function record(
  s: ProgressState,
  rec: Omit<SessionRecord, 'at' | 'channels'>,
  channels: Channel[],
  minutes: number,
): ProgressState {
  const at = Date.now();
  const today = dayKey(at);
  if (s.lastSessionDay !== today) {
    const yesterday = dayKey(at - 86400000);
    s.streak = s.lastSessionDay === yesterday ? s.streak + 1 : 1;
    s.lastSessionDay = today;
  }
  s.sessions.push({
    at,
    ...rec,
    channels: Object.fromEntries(channels.map((c) => [c.id, c.rating])),
  });
  s.best.overall = Math.max(s.best.overall, rec.overall);
  s.totals.rounds += rec.rounds;
  s.totals.punches += rec.thrown;
  s.totals.minutes += minutes;
  save(s);
  return s;
}

/** Channel deltas versus the average of the previous N sessions. Drives "you improved at X". */
export function trend(s: ProgressState, channelId: string, window = 5): number | null {
  const xs = s.sessions.filter((r) => r.channels[channelId] !== undefined);
  if (xs.length < 2) return null;
  const latest = xs[xs.length - 1].channels[channelId];
  const prior = xs.slice(Math.max(0, xs.length - 1 - window), xs.length - 1);
  if (!prior.length) return null;
  const avg = prior.reduce((a, b) => a + b.channels[channelId], 0) / prior.length;
  return Math.round(latest - avg);
}
